import React, { useState, useMemo } from 'react';
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';

// Calculate if background is light or dark
function isLightBackground(hexColor?: string): boolean {
  if (!hexColor) return true; // Default to light
  const hex = hexColor.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return brightness > 155; // >155 = light background
}


// Register custom sizes with attributors - px values
const SizeStyle = Quill.import('attributors/style/size');
SizeStyle.whitelist = ['10px', '12px', '14px', '16px', '18px', '20px', '24px', '28px', '32px', '36px', '48px', '60px', '72px'];
Quill.register(SizeStyle, true);

// Register font with attributors for inline styles
const FontStyle = Quill.import('attributors/style/font');
FontStyle.whitelist = null; // Allow any font
Quill.register(FontStyle, true);

export interface TextWidgetConfig {
  content: string; // HTML content from Quill editor
  backgroundColor?: string; // Custom background color for widget
}

interface TextWidgetProps {
  config: TextWidgetConfig;
  width: number; // Grid columns (1-12)
  height: number; // Grid rows
  isEditing?: boolean; // Whether widget is in edit mode
  onChange?: (config: TextWidgetConfig) => void; // Callback to save changes
}

export function TextWidget({ config, width, height, isEditing = false, onChange }: TextWidgetProps) {
  const { content = '', backgroundColor } = config || {};
  const [localContent, setLocalContent] = useState(content);
  const [showToolbar, setShowToolbar] = useState(true);

  // Update local content when config changes
  React.useEffect(() => {
    setLocalContent(content);
  }, [content]);

  // Handle double-click to toggle toolbar
  const handleDoubleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowToolbar(!showToolbar);
  };

  // Dynamic sizing based on widget dimensions
  const area = width * height;
  const isSmall = area <= 12; // 3×4 or smaller
  const isMedium = area <= 24; // 4×6 or smaller

  const padding = isSmall ? 'p-2' : isMedium ? 'p-3' : 'p-4';

  // Quill configuration - with font family, px-based sizes, and widget background
  const modules = useMemo(() => ({
    toolbar: [
      [{ 'font': ['arial', 'times-new-roman', 'courier', 'georgia', 'verdana', 'helvetica', 'sans-serif', 'serif', 'monospace'] }],
      [{ 'size': ['10px', '12px', '14px', '16px', '18px', '20px', '24px', '28px', '32px', '36px', '48px', '60px', '72px'] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'align': [] }],
      [{ 'color': [] }, { 'background': [] }], // Text color and text highlight
      ['link'],
      ['clean']
    ],
  }), []);

  const formats = [
    'font',
    'size',
    'bold', 'italic', 'underline', 'strike',
    'list', 'bullet',
    'align',
    'color', 'background',
    'link'
  ];

  const handleChange = (newContent: string) => {
    setLocalContent(newContent);
    if (onChange) {
      onChange({ content: newContent });
    }
  };

  const isLight = isLightBackground(backgroundColor);
  const textColorClass = backgroundColor ? (isLight ? 'text-gray-900' : 'text-white') : 'text-gray-900 dark:text-white';

  // Check if content is truly empty (Quill empty is '<p><br></p>')
  const isEmpty = !localContent || localContent.trim() === '' || localContent === '<p><br></p>' || localContent === '<p></p>';

  // No content state (when not editing)
  if (!isEditing && isEmpty) {
    return (
      <div
        className={`w-full h-full border border-dashboard-widget rounded-lg shadow-sm ${padding} flex items-center justify-center`}
        style={{ backgroundColor: backgroundColor || undefined }}
      >
        <div className={`text-sm text-center ${backgroundColor ? (isLight ? 'text-gray-400' : 'text-gray-300') : 'text-gray-400 dark:text-gray-500'}`}>
          Click to add content
        </div>
      </div>
    );
  }

  // Edit mode - show Quill editor
  if (isEditing) {
    // Widget toolbar height is ~34px, need to account for it
    const widgetToolbarHeight = 34;

    return (
      <div
        className="w-full h-full border-2 border-purple-600 dark:border-purple-400 rounded-lg shadow-lg overflow-hidden flex flex-col"
        style={{ backgroundColor: backgroundColor || undefined }}
        onDoubleClick={handleDoubleClick}
      >
        <style>{`
          /* Custom font families */
          span[style*="font-family: arial"], .ql-font-arial { font-family: Arial, sans-serif !important; }
          span[style*="font-family: times-new-roman"], .ql-font-times-new-roman { font-family: 'Times New Roman', serif !important; }
          span[style*="font-family: courier"], .ql-font-courier { font-family: 'Courier New', monospace !important; }
          span[style*="font-family: georgia"], .ql-font-georgia { font-family: Georgia, serif !important; }
          span[style*="font-family: verdana"], .ql-font-verdana { font-family: Verdana, sans-serif !important; }
          span[style*="font-family: helvetica"], .ql-font-helvetica { font-family: Helvetica, sans-serif !important; }

          /* Font dropdown labels */
          .ql-picker.ql-font .ql-picker-label[data-value="arial"]::before,
          .ql-picker.ql-font .ql-picker-item[data-value="arial"]::before { content: 'Arial' !important; }
          .ql-picker.ql-font .ql-picker-label[data-value="times-new-roman"]::before,
          .ql-picker.ql-font .ql-picker-item[data-value="times-new-roman"]::before { content: 'Times New Roman' !important; }
          .ql-picker.ql-font .ql-picker-label[data-value="courier"]::before,
          .ql-picker.ql-font .ql-picker-item[data-value="courier"]::before { content: 'Courier' !important; }
          .ql-picker.ql-font .ql-picker-label[data-value="georgia"]::before,
          .ql-picker.ql-font .ql-picker-item[data-value="georgia"]::before { content: 'Georgia' !important; }
          .ql-picker.ql-font .ql-picker-label[data-value="verdana"]::before,
          .ql-picker.ql-font .ql-picker-item[data-value="verdana"]::before { content: 'Verdana' !important; }
          .ql-picker.ql-font .ql-picker-label[data-value="helvetica"]::before,
          .ql-picker.ql-font .ql-picker-item[data-value="helvetica"]::before { content: 'Helvetica' !important; }
          .ql-picker.ql-font .ql-picker-label[data-value="sans-serif"]::before,
          .ql-picker.ql-font .ql-picker-item[data-value="sans-serif"]::before { content: 'Sans Serif' !important; }
          .ql-picker.ql-font .ql-picker-label[data-value="serif"]::before,
          .ql-picker.ql-font .ql-picker-item[data-value="serif"]::before { content: 'Serif' !important; }
          .ql-picker.ql-font .ql-picker-label[data-value="monospace"]::before,
          .ql-picker.ql-font .ql-picker-item[data-value="monospace"]::before { content: 'Monospace' !important; }

          /* Font dropdown - make wider for long names like Times New Roman */
          .widget-quill-editor .ql-picker.ql-font {
            min-width: 160px !important;
          }
          .widget-quill-editor .ql-picker.ql-font .ql-picker-label {
            padding-right: 24px !important;
            white-space: nowrap !important;
          }
          .widget-quill-editor .ql-picker.ql-font .ql-picker-options {
            min-width: 160px !important;
          }

          /* Size dropdown - limit visible items and show scrollbar */
          .widget-quill-editor .ql-picker.ql-size .ql-picker-options {
            max-height: 180px !important;
            overflow-y: auto !important;
          }

          /* Size dropdown labels - px values only, no descriptions */
          .ql-picker.ql-size .ql-picker-label[data-value="10px"]::before,
          .ql-picker.ql-size .ql-picker-item[data-value="10px"]::before { content: '10px' !important; }
          .ql-picker.ql-size .ql-picker-label[data-value="12px"]::before,
          .ql-picker.ql-size .ql-picker-item[data-value="12px"]::before { content: '12px' !important; }
          .ql-picker.ql-size .ql-picker-label[data-value="14px"]::before,
          .ql-picker.ql-size .ql-picker-item[data-value="14px"]::before { content: '14px' !important; }
          .ql-picker.ql-size .ql-picker-label[data-value="16px"]::before,
          .ql-picker.ql-size .ql-picker-item[data-value="16px"]::before { content: '16px' !important; }
          .ql-picker.ql-size .ql-picker-label[data-value="18px"]::before,
          .ql-picker.ql-size .ql-picker-item[data-value="18px"]::before { content: '18px' !important; }
          .ql-picker.ql-size .ql-picker-label[data-value="20px"]::before,
          .ql-picker.ql-size .ql-picker-item[data-value="20px"]::before { content: '20px' !important; }
          .ql-picker.ql-size .ql-picker-label[data-value="24px"]::before,
          .ql-picker.ql-size .ql-picker-item[data-value="24px"]::before { content: '24px' !important; }
          .ql-picker.ql-size .ql-picker-label[data-value="28px"]::before,
          .ql-picker.ql-size .ql-picker-item[data-value="28px"]::before { content: '28px' !important; }
          .ql-picker.ql-size .ql-picker-label[data-value="32px"]::before,
          .ql-picker.ql-size .ql-picker-item[data-value="32px"]::before { content: '32px' !important; }
          .ql-picker.ql-size .ql-picker-label[data-value="36px"]::before,
          .ql-picker.ql-size .ql-picker-item[data-value="36px"]::before { content: '36px' !important; }
          .ql-picker.ql-size .ql-picker-label[data-value="48px"]::before,
          .ql-picker.ql-size .ql-picker-item[data-value="48px"]::before { content: '48px' !important; }
          .ql-picker.ql-size .ql-picker-label[data-value="60px"]::before,
          .ql-picker.ql-size .ql-picker-item[data-value="60px"]::before { content: '60px' !important; }
          .ql-picker.ql-size .ql-picker-label[data-value="72px"]::before,
          .ql-picker.ql-size .ql-picker-item[data-value="72px"]::before { content: '72px' !important; }

          .widget-quill-editor .ql-toolbar {
            display: ${showToolbar ? 'block' : 'none'} !important;
            border: none !important;
            border-bottom: 1px solid #e5e7eb !important;
            background: #f9fafb;
            padding: 8px !important;
          }
          .widget-quill-editor.dark .ql-toolbar {
            background: #1f2937;
            border-bottom-color: #374151 !important;
          }
          .widget-quill-editor .ql-container {
            border: none !important;
            height: calc(100% - ${showToolbar ? '42px' : '0px'}) !important;
          }
          .widget-quill-editor .ql-editor {
            height: 100% !important;
            overflow-y: auto !important;
            font-size: 14px;
            padding: 12px !important;
            color: ${backgroundColor ? (isLight ? '#111827' : '#ffffff') : '#111827'} !important;
          }
          .widget-quill-editor .ql-editor.ql-blank::before {
            color: ${backgroundColor ? (isLight ? '#9ca3af' : '#d1d5db') : '#9ca3af'};
            font-style: normal;
            left: 12px;
          }
          .widget-quill-editor .ql-toolbar button {
            width: auto !important;
          }
          .widget-quill-editor .ql-toolbar .ql-stroke {
            stroke: #6b7280;
          }
          .widget-quill-editor .ql-toolbar .ql-fill {
            fill: #6b7280;
          }
          .widget-quill-editor .ql-toolbar button:hover .ql-stroke {
            stroke: #8b5cf6;
          }
          .widget-quill-editor .ql-toolbar button:hover .ql-fill {
            fill: #8b5cf6;
          }
          .widget-quill-editor .ql-toolbar button.ql-active .ql-stroke {
            stroke: #8b5cf6;
          }
          .widget-quill-editor .ql-toolbar button.ql-active .ql-fill {
            fill: #8b5cf6;
          }
        `}</style>
        {/* Spacer for widget toolbar */}
        <div style={{ height: `${widgetToolbarHeight}px` }} className="flex-shrink-0" />

        {showToolbar && (
          <div className="absolute top-10 right-2 z-10 bg-blue-50 dark:bg-blue-900/80 border border-blue-200 dark:border-blue-700 rounded px-2 py-1">
            <p className="text-xs text-blue-700 dark:text-blue-300">
              Double-click to hide toolbar
            </p>
          </div>
        )}

        <div className="flex-1 overflow-hidden">
          <ReactQuill
            theme="snow"
            value={localContent}
            onChange={handleChange}
            modules={modules}
            formats={formats}
            placeholder="Start typing... Double-click to toggle toolbar"
            className="widget-quill-editor"
            style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
          />
        </div>
      </div>
    );
  }

  // Display mode - show formatted content
  return (
    <div
      className={`w-full h-full border border-dashboard-widget rounded-lg shadow-sm ${padding} overflow-auto`}
      style={{ backgroundColor: backgroundColor || undefined }}
    >
      <style>{`
        /* Custom font families for display mode */
        span[style*="font-family: arial"] { font-family: Arial, sans-serif !important; }
        span[style*="font-family: times-new-roman"] { font-family: 'Times New Roman', serif !important; }
        span[style*="font-family: courier"] { font-family: 'Courier New', monospace !important; }
        span[style*="font-family: georgia"] { font-family: Georgia, serif !important; }
        span[style*="font-family: verdana"] { font-family: Verdana, sans-serif !important; }
        span[style*="font-family: helvetica"] { font-family: Helvetica, sans-serif !important; }
      `}</style>
      <div
        className={`ql-editor ${textColorClass}`}
        dangerouslySetInnerHTML={{ __html: localContent }}
        style={{ padding: 0 }}
      />
    </div>
  );
}
